import type { RegisteredComponent } from "@builder.io/sdk-react";

import { FeaturedThreeUp } from "~/hec/components/organisms/featured-three-up/featured-three-up";
import { hasVisibleText, stripAnchors } from "~/hec/utils/rich-text";
import type { SiteId } from "~/hec/utils/site-id";
import { useRootLayoutData } from "~/root";

import { toFeatureLinkTarget } from "../feature-link/feature-link.logic";
import { featuredThreeUpInputs } from "./inputs";
import type { BuilderFeaturedThreeUpProps } from "./types";

/**
 * Featured 3-Up (COM-945): replaces the "Featured 3-Up" symbol. Thin wrapper:
 * resolves each tile's link and spreads `attributes` on the root (`noWrap`
 * contract, CLAUDE.md §4).
 */
const BuilderFeaturedThreeUp = ({
  heading,
  items = [],
  attributes,
}: BuilderFeaturedThreeUpProps) => {
  const { site } = useRootLayoutData();
  const siteId = site.id as SiteId;
  return (
    <FeaturedThreeUp
      heading={heading}
      items={items.map(item => ({
        desktopImage: item.desktopImage,
        mobileImage: item.mobileImage,
        label: hasVisibleText(item.label)
          ? stripAnchors(item.label)
          : undefined,
        whiteText: item.whiteText !== false,
        link: toFeatureLinkTarget(item, siteId),
      }))}
      {...attributes}
    />
  );
};

export const FeaturedThreeUpDefinition = {
  component: BuilderFeaturedThreeUp,
  name: "FeaturedThreeUp",
  // Label only — safe to rename. `name` above is the published contract.
  friendlyName: "Featured 3-Up",
  noWrap: true,
  canHaveChildren: false,
  image: "https://unpkg.com/lucide-static@0.537.0/icons/columns-3.svg",
  inputs: featuredThreeUpInputs,
} satisfies RegisteredComponent;

export default BuilderFeaturedThreeUp;
